import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Printer, Download, ArrowLeft } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSettings } from '../../hooks/useSettings';
import InvoiceRenderer from './invoice-templates/InvoiceRenderer';
import { InvoiceData, HospitalInfo } from './invoice-templates/types';
import { getTemplateComponent } from './invoice-templates/TemplateRegistry';

export default function InvoicePrint() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        setInvoice(getMockInvoice());
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('invoices')
        .select(`
          *,
          patient:patients(*),
          items:invoice_items(
            id,
            description,
            quantity,
            unit_price,
            total
          )
        `)
        .eq('id', id)
        .order('created_at', { foreignTable: 'invoice_items', ascending: true })
        .single();

      if (fetchError) throw fetchError;

      if (!data) {
        setError('Invoice not found');
        setLoading(false);
        return;
      }

      const items = (data.items ?? []).map((item: any) => ({
        id: item.id,
        description: item.description ?? '',
        quantity: Number(item.quantity) || 0,
        unit_price: Number(item.unit_price) || 0,
        total: Number(item.total) || (Number(item.quantity) || 0) * (Number(item.unit_price) || 0),
      }));

      const invoiceData: InvoiceData = {
        id: data.id,
        invoice_number: data.invoice_number,
        issue_date: data.issue_date,
        due_date: data.due_date,
        patient: {
          id: data.patient.id,
          name: data.patient.name,
          unique_id: data.patient.unique_id,
          phone: data.patient.phone,
          email: data.patient.email,
          address: data.patient.address,
          age: data.patient.age,
          gender: data.patient.gender,
        },
        items: items,
        subtotal: data.subtotal || 0,
        discount_amount: data.discount_amount || 0,
        tax_amount: data.tax_amount || 0,
        total: data.total || 0,
        amount_paid: data.amount_paid || 0,
        balance_due: data.balance_due || 0,
        status: data.status,
        payment_method: data.payment_method,
        reference_type: data.reference_type,
        reference_id: data.reference_id,
        notes: data.notes,
      };

      setInvoice(invoiceData);
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const getMockInvoice = (): InvoiceData => {
    return {
      id: id || '1',
      invoice_number: 'INV-2024-001',
      issue_date: new Date().toISOString(),
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      patient: {
        id: '1',
        name: 'John Doe',
        unique_id: 'P-001',
        phone: '+880 1711-123456',
        email: 'john.doe@example.com',
        address: 'Dhaka, Bangladesh',
        age: 35,
        gender: 'Male',
      },
      items: [
        {
          id: '1',
          description: 'General Consultation',
          quantity: 1,
          unit_price: 500,
          total: 500,
        },
        {
          id: '2',
          description: 'Blood Test - Complete Blood Count',
          quantity: 1,
          unit_price: 800,
          total: 800,
        },
        {
          id: '3',
          description: 'X-Ray Chest',
          quantity: 1,
          unit_price: 1200,
          total: 1200,
        },
      ],
      subtotal: 2500,
      discount_amount: 100,
      tax_amount: 0,
      total: 2400,
      amount_paid: 2400,
      balance_due: 0,
      status: 'paid',
      payment_method: 'Cash',
      notes: 'Thank you for choosing our services.',
    };
  };

  const getHospitalInfo = (): HospitalInfo => {
    return {
      name: settings.hospital_name_en || 'Medical Center',
      address: settings.address || 'Dhaka, Bangladesh',
      phone: settings.phone || '+880 1711-000000',
      email: settings.email || 'info@hospital.com',
      website: settings.website || undefined,
      logo: settings.logo_url || undefined,
      headerImage: settings.report_header_url || undefined,
      banglaSubtitle: settings.hospital_name_bn || undefined,
    };
  };

  const openPdfViewer = async (download = false) => {
    try {
      await document.fonts?.ready;
      
      const el = pageRef.current;
      if (!el) {
        console.error('Page element not found');
        return;
      }

      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: el.scrollWidth,
        allowTaint: false,
      });

      const imgData = canvas.toDataURL('image/png');
      
      const doc = new jsPDF({
        unit: 'mm',
        format: isSmall ? [80, 297] : 'a4',
        orientation: 'p',
      });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageW - margin * 2;
      const contentHeight = pageH - margin * 2;
      
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      doc.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);

      if (download) {
        doc.save(`Invoice-${invoice?.invoice_number || invoice?.id || 'document'}.pdf`);
      } else {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Falling back to browser print.');
      window.print();
    }
  };

  const handlePrint = () => {
    if (settings.print_mode === 'browser_print') {
      window.print();
    } else {
      openPdfViewer(false);
    }
  };

  const handleDownloadPDF = () => {
    openPdfViewer(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Invoice not found'}</p>
          <button
            onClick={() => navigate('/billings/invoices')}
            className="text-primary-600 hover:text-primary-700"
          >
            ← Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  const templateParam = searchParams.get('template');
  const selectedTemplateId = templateParam || settings.invoice_template || 'a4';
  const isSmall = selectedTemplateId.includes('small');

  const handleTemplateChange = (newTemplate: string) => {
    searchParams.set('template', newTemplate);
    setSearchParams(searchParams, { replace: true });
  };

  const printCss = `
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        height: auto !important;
      }
      #root, #print-root {
        margin: 0 !important;
        padding: 0 !important;
      }
      .print\\:hidden, nav, aside, header, footer {
        display: none !important;
      }
      @page {
        size: ${isSmall ? '80mm 297mm' : 'A4'};
        margin: 12mm;
      }
      .print-page {
        width: ${isSmall ? '80mm' : '210mm'};
        min-height: calc(297mm - 24mm);
        margin: 0 auto;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color-adjust: exact;
        background: #fff !important;
      }
      table {
        border-collapse: collapse !important;
      }
      th, td {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .avoid-break, table, tr, img, h1, h2, .totals {
        break-inside: avoid-page;
        page-break-inside: avoid;
      }
      .page-break {
        break-after: page;
        page-break-after: always;
      }
    }
    .print-page {
      width: ${isSmall ? '80mm' : '210mm'};
      margin: 0 auto;
    }
  `;

  return (
    <div id="print-root">
      {/* Print Controls - Hidden when printing */}
      <div className="print-toolbar print:hidden bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/billings/invoices')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Invoices
          </button>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Template:</label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={selectedTemplateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="a4">A4 (Header)</option>
              <option value="a4-no-header">A4 (No Header)</option>
              <option value="small">Small (80mm)</option>
              <option value="small-no-header">Small (No Header)</option>
              <option value="customer-copy">Customer Copy</option>
              <option value="header-upload">Header Image Upload</option>
              <option value="theme-7">QR Variant</option>
              <option value="theme-8">Watermark</option>
            </select>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Printer className="h-4 w-4" />
              Print Invoice
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Template */}
      <div className="print:p-0 bg-gray-100 min-h-screen py-8">
        <div ref={pageRef} className="print-page">
          <InvoiceRenderer
            invoice={invoice}
            hospital={getHospitalInfo()}
            templateId={selectedTemplateId}
          />
        </div>
      </div>

      {/* Print Styles */}
      <style>{printCss}</style>
    </div>
  );
}
